import React from "react";

type SidebarMode = "expanded" | "collapsed" | "overlay";

interface SidebarLayoutContextValue {
  isCollapsed: boolean;
  sidebarMode: SidebarMode;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
}

const SidebarLayoutContext = React.createContext<SidebarLayoutContextValue | undefined>(undefined);

const STORAGE_KEY = "sidebarCollapsed";
const TABLET_BREAKPOINT = 1024;
const MOBILE_BREAKPOINT = 768;

const readInitialCollapsed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const getSidebarMode = (width: number): SidebarMode => {
  if (width <= MOBILE_BREAKPOINT) return "overlay";
  if (width <= TABLET_BREAKPOINT) return "collapsed";
  return "expanded";
};

export const SidebarLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(() => {
    if (typeof window !== "undefined" && window.innerWidth <= TABLET_BREAKPOINT) {
      return true;
    }
    return readInitialCollapsed();
  });

  const [sidebarMode, setSidebarMode] = React.useState<SidebarMode>(() => {
    if (typeof window === "undefined") return "expanded";
    return getSidebarMode(window.innerWidth);
  });

  React.useEffect(() => {
    const handleResize = () => {
      const mode = getSidebarMode(window.innerWidth);
      setSidebarMode(mode);

      if (mode === "overlay" || mode === "collapsed") {
        setIsCollapsed(true);
        try {
          localStorage.setItem(STORAGE_KEY, "true");
        } catch {
          // no-op
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setCollapsed = React.useCallback((next: boolean) => {
    setIsCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // no-op
    }
  }, []);

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(!isCollapsed);
  }, [isCollapsed, setCollapsed]);

  const value = React.useMemo(
    () => ({ isCollapsed, sidebarMode, setCollapsed, toggleCollapsed }),
    [isCollapsed, sidebarMode, setCollapsed, toggleCollapsed]
  );

  return <SidebarLayoutContext.Provider value={value}>{children}</SidebarLayoutContext.Provider>;
};

export const useSidebarLayout = (): SidebarLayoutContextValue => {
  const ctx = React.useContext(SidebarLayoutContext);
  if (!ctx) {
    throw new Error("useSidebarLayout must be used within SidebarLayoutProvider");
  }
  return ctx;
};
